import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { SubscriptionPlanService, AdminPlan } from 'src/app/subscription-management/subscription-services/subscription-plan.service';
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore';

interface UserDetails {
  uid: string;
}

// interface UserProfile {
//   displayName: string;
//   role?: string;
//   photoURL?: string;
//   accountdetails?: { companyname?: string };
//   plan?: string;
//   planName?: string;
//   subscriptionExpiry?: string;
//   subscriptionStart?: string;
//   term?: string;
//   manualBookingsUsed?: number;
// }
interface UserProfile {
  displayName: string;
  role?: string;
  photoURL?: string;
  accountdetails?: { companyname?: string };
  plan?: string;
  planId?: string;        // ← ADD THIS
  planName?: string;
  subscriptionExpiry?: string;
  subscriptionStart?: string;
  term?: string;
  manualBookingsUsed?: number;
}
interface MenuItem {
  icon: string;
  label: string;
  route: string;
  active?: boolean;
  badge?: number;
}

interface SidebarPlanInfo {
  name: string;
  tagline: string;
  color: string;
  marketplaceEnabled: boolean;
  activeListingLimit: number;
  activeListings: number;
  renewalDate: Date | null;
  listingPercent: number;
}

@Component({
  selector: 'app-servicemanager-layout',
  templateUrl: './servicemanager-layout.component.html',
  styleUrls: ['./servicemanager-layout.component.scss'],
})
export class ServicemanagerLayoutComponent implements OnInit, OnDestroy {
  userdetail!: UserDetails;
  currentuid: string = '';
  userObj: UserProfile = { displayName: '' };
  currentuserrole: string = '';
  currentuserprofile: string = '';
  currentusername: string = '';
  isMobileView = false;
  sidebarOpen = true;
  lang = 'en';
  today = new Date();

  sidebarPlan: SidebarPlanInfo | null = null;
  planBadgeColor: string = '';
  planBadgeName: string = '';
  planLoading = false;

  private planSub?: Subscription;

  menuItems: MenuItem[] = [
    { icon: '📊', label: 'DASHBOARD', route: '/servicemanager/ad-dashboard', active: true },
    { icon: '🚗', label: 'FLEET', route: '/servicemanager/fleet' },
    { icon: '👥', label: 'USERS', route: '/servicemanager/employeeslist' },
    { icon: '📋', label: 'DEFECT_REPORT', route: '/servicemanager/defectreportlist' },
    { icon: '🛠️', label: 'DEFECT_CHECKLIST', route: '/servicemanager/defectchecklist' },
    { icon: '📦', label: 'INVENTORY', route: '/servicemanager/inventorylist' },
  ];

  constructor(
    private firestoreService: FirestoreService,
    private firestore: Firestore,
    private router: Router,
    private translateService: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private planService: SubscriptionPlanService
  ) { }

  ngOnInit(): void {
    this.lang = localStorage.getItem('lang') || 'en';
    this.translateService.use(this.lang);
    this.getCurrentUser();

    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobileView = result.matches;
      this.sidebarOpen = !result.matches;
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobileView) this.sidebarOpen = false;
      });
  }

  ngOnDestroy(): void {
    this.planSub?.unsubscribe();
  }

  async getCurrentUser() {
    const res = await this.firestoreService.getUserDetails() as UserDetails;
    if (!res?.uid) return;

    this.userdetail = res;
    this.currentuid = res.uid;

    const profile = await this.firestoreService.getUserProfile(this.currentuid) as UserProfile;
    if (!profile) return;

    this.userObj = profile;
    this.currentuserrole = profile.role ?? '';
    this.currentuserprofile = profile.photoURL ?? '';
    this.currentusername = profile.displayName ?? 'User';

    if (profile.planId) {
      this.planLoading = true;
      this.loadPlanData(profile);
    }
  }

  private loadPlanData(profile: UserProfile): void {
    this.planSub = this.planService.plans$.subscribe(async (plans) => {
      // Wait for actual data — BehaviorSubject emits [] initially
      if (!plans || plans.length === 0) return;

      const matched = plans.find(p => p.id === profile.planId);

      if (!matched) {
        this.planLoading = false;
        return;
      }

      // Set badge values for profile dropdown
      this.planBadgeColor = matched.color;
      this.planBadgeName = matched.name;

      // Count active listings for this vendor
      const activeListings = await this.countActiveListings(
        this.currentuid,
        matched.marketplaceEnabled
      );

      // subscriptionExpiry is stored as ISO string on this project
      const renewalDate = profile.subscriptionExpiry
        ? new Date(profile.subscriptionExpiry)
        : null;

      const limit = matched.activeListingLimit;
      const percent = limit > 0
        ? Math.min(Math.round((activeListings / limit) * 100), 100)
        : 0;

      this.sidebarPlan = {
        name: matched.name,
        tagline: matched.tagline,
        color: matched.color,
        marketplaceEnabled: matched.marketplaceEnabled,
        activeListingLimit: limit,
        activeListings,
        renewalDate,
        listingPercent: percent,
      };

      this.planLoading = false;

      // Unsubscribe after first successful load
      this.planSub?.unsubscribe();
    });
  }

  private async countActiveListings(uid: string, marketplaceEnabled: boolean): Promise<number> {
    if (!marketplaceEnabled) return 0;
    try {
      const now = new Date();
      const listingsRef = collection(this.firestore, 'listings');
      const q = query(
        listingsRef,
        where('vendorId', '==', uid),
        where('isMarketplaceActive', '==', true)
      );
      const snap = await getDocs(q);
      return snap.size;
    } catch (e) {
      console.error('[Plan] countActiveListings error:', e);
      return 0;
    }
  }

  ChangeLang(lang: any) {
    const selectedLanguage = lang.target.value;
    this.lang = selectedLanguage;
    localStorage.setItem('lang', selectedLanguage);
    this.translateService.use(selectedLanguage);
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  navigateTo(item: MenuItem) {
    this.menuItems.forEach(menuItem => (menuItem.active = false));
    item.active = true;
    this.router.navigate([item.route]);
  }

  navigateToManagePlan() {
    this.router.navigate(['/business/subscription']);
  }
}